import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
// @ts-ignore - API is a JavaScript file without type definitions
import API from '../context/API.js';

// Types
export interface SalesSummary {
  grossSales: number;
  netSales: number;
  orderCount: number;
  currency?: string;
}

export interface Period {
  startDate: string;
  endDate: string;
  month?: string;
  year?: number;
}

export interface WeeklyBreakdown {
  [key: string]: number;
}

export interface MonthlyReportData {
  merchantName?: string;
  reportType?: string;
  period?: Period;
  salesSummary: SalesSummary;
  weeklyBreakdown?: WeeklyBreakdown;
}

export interface MonthlyReportsState {
  reportData: MonthlyReportData | null;
  loading: boolean;
  error: string | null;
  stores: Array<{ id: string; name: string; [key: string]: any }>;
  storesLoading: boolean;
}

const initialState: MonthlyReportsState = {
  reportData: null,
  loading: false,
  error: null,
  stores: [],
  storesLoading: false,
};

// Async thunks
export const fetchMonthlyReport = createAsyncThunk(
  'monthlyReports/fetchMonthlyReport',
  async (
    params: {
      month: string;
      merchantId?: string;
      storeId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await API.get('/analytics/reports/monthly', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          month: params.month,
          merchantId: params.merchantId,
          store: params.storeId,
        },
      });

      return response.data.data || {};
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch monthly report'
      );
    }
  }
);

export const fetchStores = createAsyncThunk(
  'monthlyReports/fetchStores',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await API.get('/stores', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data.data || [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch stores'
      );
    }
  }
);

const monthlyReportsSlice = createSlice({
  name: 'monthlyReports',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearReportData: (state) => {
      state.reportData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch monthly report
      .addCase(fetchMonthlyReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMonthlyReport.fulfilled, (state, action) => {
        state.loading = false;
        state.reportData = action.payload;
      })
      .addCase(fetchMonthlyReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch stores
      .addCase(fetchStores.pending, (state) => {
        state.storesLoading = true;
      })
      .addCase(fetchStores.fulfilled, (state, action) => {
        state.storesLoading = false;
        state.stores = action.payload;
      })
      .addCase(fetchStores.rejected, (state, action) => {
        state.storesLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearReportData } = monthlyReportsSlice.actions;
export default monthlyReportsSlice.reducer;
